import * as React from 'react';
import clsx from 'clsx';
import { useMediaQuery } from '@mui/material';
import { Form, Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Attribution, ReviewRating } from 'models/review';

import { Stars } from '../Stars/Stars';
import { SearchFormValues } from './review.utils';
import styles from './ProductReviewsPane.module.css';
import { getReviewAttributionText } from 'lib/util/getReviewAttributionText';

import { BookingWidgetThemeContext } from 'contexts/BookingWidgetThemeContext';
import { getThemeCssOverride } from 'components/Themes';
import { ReduxState } from 'ducks';

interface Props {
  onSearchFormValuesChange: (searchFormValues: SearchFormValues) => void;
  searchFormValues: SearchFormValues;
  initialIsExpanded?: boolean;
}

export const ReviewSearchForm = ({
  onSearchFormValuesChange,
  searchFormValues,
  initialIsExpanded,
}: Props) => {
  const [isExpanded, setIsExpanded] = React.useState<boolean>(initialIsExpanded ?? true);
  const { t } = useTranslation();

  const isSmallScreen = useMediaQuery('(max-width: 600px)');
  const loading = useSelector((state: ReduxState) => state.universal.reviews.loading);

  React.useEffect(() => {
    if (isSmallScreen) {
      setIsExpanded(false);
    }
  }, [isSmallScreen]);

  const attributions: Attribution[] = ['FAMILY', 'COUPLE', 'FRIEND', 'SOLO', 'BUSINESS'];

  const monthOptions: { value: number; text: string }[] = [
    {
      value: 1,
      text: t('Jan'),
    },
    {
      value: 2,
      text: t('Feb'),
    },
    {
      value: 3,
      text: t('Mar'),
    },
    {
      value: 4,
      text: t('Apr'),
    },
    {
      value: 5,
      text: t('May'),
    },
    {
      value: 6,
      text: t('Jun'),
    },
    {
      value: 7,
      text: t('Jul'),
    },
    {
      value: 8,
      text: t('Aug'),
    },
    {
      value: 9,
      text: t('Sep'),
    },
    {
      value: 10,
      text: t('Oct'),
    },
    {
      value: 11,
      text: t('Nov'),
    },
    {
      value: 12,
      text: t('Dec'),
    },
  ];

  const languageOptions = [
    {
      value: 'EN_US',
      text: t('English'),
    },
    {
      value: 'JA_JP',
      text: t('Japanese'),
    },
  ];

  const bookingWidgetTheme = React.useContext(BookingWidgetThemeContext);

  return (
    <Form<SearchFormValues>
      initialValues={searchFormValues}
      onSubmit={async (values) => {
        await new Promise((resolve) => setTimeout(resolve, 500));
        onSearchFormValuesChange(values);
      }}
    >
      {({ handleSubmit, submitting }) => (
        <form onSubmit={handleSubmit}>
          <div className={styles['c-review__box']}>
            <a
              className={clsx(
                styles['c-review__search__ttl'],
                styles['ac'],
                isExpanded ? styles['is-open'] : styles['is-close']
              )}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <img src="/static/images/ic_search_black.svg" />
              {t('Search')}
              <p className={styles['c-review__close']}></p>
            </a>
            <div
              className={clsx(
                styles['c-review__search__main'],
                styles['ac'],
                isExpanded ? styles['is-open'] : styles['is-close']
              )}
            >
              <div className={styles['c-review__search__main__inner']}>
                <div className={styles['c-review__search__main__item']}>
                  <p className={styles['c-review__search__main__item__ttl']}>{t('Star Rating')}</p>
                  <div className={styles['c-review__search__main__item__checkbox']}>
                    {[5, 4, 3, 2, 1].map((rating) => (
                      <Field<ReviewRating>
                        name="ratings"
                        type="checkbox"
                        key={`REVIEW_RATING_${rating}`}
                        value={`REVIEW_RATING_${rating}` as ReviewRating}
                      >
                        {({ input }) => (
                          <label>
                            <input {...input} />
                            <p></p>
                            <div
                              className={styles['c-review__search__main__item__checkbox__stars']}
                            >
                              <Stars rating={rating} />
                            </div>
                          </label>
                        )}
                      </Field>
                    ))}
                  </div>
                </div>
                <div className={styles['c-review__search__main__item']}>
                  <p className={styles['c-review__search__main__item__ttl']}>
                    {t('Participation Type')}
                  </p>
                  <div className={styles['c-review__search__main__item__checkbox']}>
                    {attributions.map((attribution) => (
                      <Field<Attribution>
                        key={attribution}
                        name="attributions"
                        type="checkbox"
                        value={attribution}
                      >
                        {({ input }) => (
                          <label>
                            <input {...input} />
                            <p></p>
                            <span>{getReviewAttributionText(attribution, t)}</span>
                          </label>
                        )}
                      </Field>
                    ))}
                  </div>
                </div>
                <div className={styles['c-review__search__main__item']}>
                  {/*
          <div className={styles['c-review__search__main__item__col']}>
            <p className={styles['c-review__search__main__item__ttl']}>キーワード</p>
            <input
              type="text"
              className={styles['c-review__search__main__item__inputText']}
              placeholder="キーワードを入力"
              name="keyword"
              value=""
            />
          </div>
          */}
                  <div className={styles['c-review__search__main__item__col']}>
                    <p className={styles['c-review__search__main__item__ttl']}>
                      {t('Participation Period')}
                    </p>
                    <div className={styles['c-review__search__main__item__flex']}>
                      <Field<SearchFormValues['monthStart']> name="monthStart">
                        {({ input }) => (
                          <label className={styles['c-review__search__main__item__select']}>
                            <select {...input}>
                              {monthOptions.map((monthOption) => (
                                <option key={monthOption.value} value={monthOption.value}>
                                  {monthOption.text}
                                </option>
                              ))}
                            </select>
                          </label>
                        )}
                      </Field>
                      <p>～</p>
                      <Field<SearchFormValues['monthEnd']> name="monthEnd">
                        {({ input }) => (
                          <label className={styles['c-review__search__main__item__select']}>
                            <select {...input}>
                              {monthOptions.map((monthOption) => (
                                <option key={monthOption.value} value={monthOption.value}>
                                  {monthOption.text}
                                </option>
                              ))}
                            </select>
                          </label>
                        )}
                      </Field>
                    </div>
                  </div>
                  <div className={styles['c-review__search__main__item__col']}>
                    <p className={styles['c-review__search__main__item__ttl']}>{t('Language')}</p>
                    <Field<SearchFormValues['language']> name="language">
                      {({ input }) => (
                        <label className={styles['c-review__search__main__item__select']}>
                          <select {...input}>
                            <option value="">{t('All Languages')}</option>
                            {languageOptions.map((languageOption) => (
                              <option key={languageOption.value} value={languageOption.value}>
                                {languageOption.text}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}
                    </Field>
                  </div>
                </div>
              </div>
              <button
                className={clsx(
                  styles['c-review__search__main__submit'],
                  submitting && styles['loading'],
                  getThemeCssOverride(bookingWidgetTheme, 'c-review__search__main__submit')
                )}
                disabled={loading}
                type="submit"
              >
                {t('Search')}
              </button>
            </div>
          </div>
        </form>
      )}
    </Form>
  );
};
